import { useCallback, useEffect, useRef, useState } from 'react';

import { JSONSchema7Definition } from 'json-schema';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';

import { basePath } from '~/services/ToursService';
import { request } from '~/services/common';

import { ArrayItemCopyDefinition } from './ArrayItemCopyAndReplaceModal';
import { ImagePreviewDefinition } from './ImagePreviewModal';
import { ParentSiblingArrayCopyDefinition } from './ParentSiblingArrayCopyModal';
import { cloneFormData } from './formUtils';

export interface ExtendedRJSFormContext {
  triggerSiblingParentArrayCopy: (definition: {
    fieldDepth: Array<string>;
    fieldTitleProperty?: string;
    canCopyArrayItemsFromSimilar?: string;
    uncopyableProperties?: Array<string>;
    properties: Record<string, JSONSchema7Definition>;
  }) => void;
  triggerArrayItemCopy: (definition: {
    currentItemIndex: number;
    fieldDepth: Array<string>;
    fieldTitleProperty?: string;
    uncopyableProperties?: Array<string>;
  }) => void;
  triggerImagePreview: (definition: ImagePreviewDefinition) => void;
  pushToFormDataArrayUpdateQueue: (fieldDepth: Array<string>, items: Array<unknown>) => void;
  setFormDataAtPath: (fieldDepth: Array<string>, value: unknown, meta?: FormDataChangeMeta) => void;
  handleInclusionItemsGenerate: (tourOptionIndex: number) => void;
  reorderArrayItems: (fieldDepth: string[], fromIndex: number, toIndex: number) => void;
  tourId: string;
  paymentScheduleTemplateId?: string | null;
  paymentScheduleTemplateIds: string[];
  useSharedImageArrayEditor?: boolean;
}

interface FormDataChangeMeta {
  fieldDepth?: Array<string>;
  source: string;
}

interface ExtendedRJSFormHook<T> {
  formData: T;
  setFormData: (formData: T, meta?: FormDataChangeMeta) => void;
  formContext: ExtendedRJSFormContext;
  parentSiblingArrayCopyDefinition?: ParentSiblingArrayCopyDefinition;
  handleParentSiblingArrayCopyDismiss: () => void;
  handleParentSiblingArrayCopy: (targetDepth: Array<string>, items: Array<unknown>) => void;
  arrayItemCopyDefinition?: ArrayItemCopyDefinition;
  handleArrayItemCopyDismiss: () => void;
  handleArrayItemCopy: (targetDepth: Array<string>, item: unknown) => void;
  imagePreviewDefinition: ImagePreviewDefinition;
  handleImagePreviewDismiss: () => void;
}

export default function useExtendedRJSForm<T = unknown>(
  initialFormData: T,
  options?: {
    onFormDataChange?: (payload: { formData: T; meta: FormDataChangeMeta }) => void;
    useSharedImageArrayEditor?: boolean;
  },
): ExtendedRJSFormHook<T> {
  const [formData, setFormData] = useState<T>(initialFormData);
  const pendingFormDataChangeMetaRef = useRef<FormDataChangeMeta[]>([]);

  useEffect(() => {
    pendingFormDataChangeMetaRef.current = [];
    setFormData(initialFormData);
  }, [initialFormData]);

  const onFormDataChange = options?.onFormDataChange;

  useEffect(() => {
    if (pendingFormDataChangeMetaRef.current.length === 0) {
      return;
    }

    const pendingMeta = pendingFormDataChangeMetaRef.current;
    pendingFormDataChangeMetaRef.current = [];

    pendingMeta.forEach((meta) => {
      onFormDataChange?.({ formData, meta });
    });
  }, [formData, onFormDataChange]);

  const updateFormData = useCallback((nextFormData: T, meta: FormDataChangeMeta = { source: 'unknown' }) => {
    pendingFormDataChangeMetaRef.current.push(meta);
    setFormData(nextFormData);
  }, []);

  const updateFormDataAtLatestState = useCallback(
    (mutateFormData: (formDataCopy: T) => void, meta: FormDataChangeMeta) => {
      pendingFormDataChangeMetaRef.current.push(meta);
      setFormData((currentFormData) => {
        const formDataCopy = cloneDeep(currentFormData);
        mutateFormData(formDataCopy);
        return formDataCopy;
      });
    },
    [],
  );

  const pushToFormDataArrayUpdateQueue = useCallback(
    (fieldDepth: Array<string>, items: Array<unknown>) => {
      updateFormDataAtLatestState(
        (formDataCopy) => {
          const target = get(formDataCopy, fieldDepth);

          cloneFormData(items).forEach((item) => {
            target.push(item);
          });
        },
        { fieldDepth, source: 'push-to-form-data-array-update-queue' },
      );
    },
    [updateFormDataAtLatestState],
  );

  const setFormDataAtPath = useCallback(
    (fieldDepth: Array<string>, value: unknown, meta?: FormDataChangeMeta) => {
      updateFormDataAtLatestState((formDataCopy) => {
        set(formDataCopy as object, fieldDepth, cloneFormData(value));
      }, meta ?? { fieldDepth, source: 'set-form-data-at-path' });
    },
    [updateFormDataAtLatestState],
  );

  // PARENT SIBLING ARRAY COPY
  const [parentSiblingArrayCopyDefinition, setParentSiblingArrayCopyDefinition] =
    useState<ParentSiblingArrayCopyDefinition>(undefined);

  const triggerSiblingParentArrayCopy = useCallback<ExtendedRJSFormContext['triggerSiblingParentArrayCopy']>(
    ({ fieldDepth, uncopyableProperties, canCopyArrayItemsFromSimilar, fieldTitleProperty, properties }) => {
      setParentSiblingArrayCopyDefinition({
        fieldDepth,
        parentTitleProperty: fieldTitleProperty,
        canCopyArrayItemsFromSimilar,
        uncopyableProperties,
        properties,
      });
    },
    [],
  );

  const handleParentSiblingArrayCopy = useCallback<ExtendedRJSFormHook<T>['handleParentSiblingArrayCopy']>(
    (targetDepth, items) => {
      updateFormDataAtLatestState(
        (formDataCopy) => {
          const target = get(formDataCopy, targetDepth);
          items.forEach((item) => {
            target.push(item);
          });
        },
        { fieldDepth: targetDepth, source: 'handle-parent-sibling-array-copy' },
      );

      setParentSiblingArrayCopyDefinition(undefined);
    },
    [updateFormDataAtLatestState],
  );

  const handleInclusionItemsGenerate = useCallback<ExtendedRJSFormContext['handleInclusionItemsGenerate']>(
    async (tourOptionIndex: number) => {
      const formDataCopy = cloneDeep(formData);
      const tourOptionId = (formDataCopy as any).tourOptions[tourOptionIndex].id;
      const existingInclusions = (formDataCopy as any).tourOptions[tourOptionIndex].inclusions;
      if (existingInclusions === null || existingInclusions === undefined || existingInclusions.length === 0) {
        throw new Error('No existing highlights & inclusions to generate from');
      }
      const response = await request(`${basePath()}/ai/tourOption/${tourOptionId}/inclusionItems`, {
        method: 'GET',
      });
      updateFormDataAtLatestState(
        (latestFormData) => {
          (latestFormData as any).tourOptions[tourOptionIndex].inclusionItems.push(...response.result);
        },
        { source: 'handle-inclusion-items-generate' },
      );
    },
    [formData, updateFormDataAtLatestState],
  );

  const handleParentSiblingArrayCopyDismiss = useCallback(() => {
    setParentSiblingArrayCopyDefinition(undefined);
  }, []);

  // ARRAY ITEM COPY
  const [arrayItemCopyDefinition, setArrayItemCopyDefinition] = useState<ArrayItemCopyDefinition>(undefined);
  const triggerArrayItemCopy = useCallback<ExtendedRJSFormContext['triggerArrayItemCopy']>(
    ({ currentItemIndex, fieldDepth, uncopyableProperties, fieldTitleProperty }) => {
      setArrayItemCopyDefinition({
        currentItemIndex,
        fieldDepth,
        itemTitleProperty: fieldTitleProperty,
        uncopyableProperties,
      });
    },
    [],
  );

  const handleArrayItemCopy = useCallback<ExtendedRJSFormHook<T>['handleArrayItemCopy']>(
    (targetDepth, item) => {
      updateFormDataAtLatestState(
        (formDataCopy) => {
          set(formDataCopy as object, targetDepth, cloneFormData(item));
        },
        { fieldDepth: targetDepth, source: 'handle-array-item-copy' },
      );
      setArrayItemCopyDefinition(undefined);
    },
    [updateFormDataAtLatestState],
  );

  const handleArrayItemCopyDismiss = useCallback(() => {
    setArrayItemCopyDefinition(undefined);
  }, []);

  // IMAGE PREVIEW
  const [imagePreviewDefinition, setImagePreviewDefinition] = useState<ImagePreviewDefinition | undefined>(undefined);

  const handleImagePreviewDismiss = useCallback(() => {
    setImagePreviewDefinition(undefined);
  }, []);

  const triggerImagePreview = useCallback((definition: ImagePreviewDefinition) => {
    setImagePreviewDefinition(definition);
  }, []);

  const reorderArrayItems = useCallback(
    (fieldDepth: string[], fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;

      updateFormDataAtLatestState(
        (formDataCopy) => {
          const array = get(formDataCopy, fieldDepth) as unknown[];

          if (
            !Array.isArray(array) ||
            fromIndex < 0 ||
            toIndex < 0 ||
            fromIndex >= array.length ||
            toIndex >= array.length
          ) {
            return;
          }

          const [movedItem] = array.splice(fromIndex, 1);
          array.splice(toIndex, 0, movedItem);
        },
        { fieldDepth, source: 'reorder-array-items' },
      );
    },
    [updateFormDataAtLatestState],
  );

  return {
    formData,
    setFormData: updateFormData,
    parentSiblingArrayCopyDefinition,
    handleParentSiblingArrayCopy,
    handleParentSiblingArrayCopyDismiss,
    arrayItemCopyDefinition,
    handleArrayItemCopy,
    handleArrayItemCopyDismiss,
    imagePreviewDefinition,
    handleImagePreviewDismiss,
    formContext: {
      triggerSiblingParentArrayCopy,
      triggerArrayItemCopy,
      triggerImagePreview,
      pushToFormDataArrayUpdateQueue,
      setFormDataAtPath,
      handleInclusionItemsGenerate,
      reorderArrayItems,
      tourId: (formData as any)?.tour?.id,
      paymentScheduleTemplateId: (formData as any)?.tour?.paymentSchedule?.paymentScheduleTemplateId,
      paymentScheduleTemplateIds:
        (formData as any)?.tour?.paymentSchedule?.paymentScheduleTemplateIds ??
        ((formData as any)?.tour?.paymentSchedule?.paymentScheduleTemplateId
          ? [(formData as any).tour.paymentSchedule.paymentScheduleTemplateId]
          : []),
      useSharedImageArrayEditor: options?.useSharedImageArrayEditor,
    },
  };
}
